import { programmeDuChapitre, programmeModifie, reglageCourant } from '@neopolis/engine';
import type { Condition, Content, GameState, ReglageProgramme } from '@neopolis/engine';

import { T } from '../../textes-interface.js';

/**
 * Étape 2 — Réparer le programme.
 *
 * L'îlot ne part pas d'une page blanche : il reçoit le programme que la ville
 * utilise aujourd'hui et le corrige. C'est la compétence C6 — comprendre et
 * modifier un programme existant — avant C9, qui viendra en le testant.
 *
 * Le programme est montré comme un texte à lire, pas comme un formulaire : les
 * lignes se suivent dans l'ordre où elles s'exécutent, et les valeurs
 * modifiables sont des listes fermées. On veut que l'élève raisonne sur « le
 * soir plutôt que midi », pas qu'il cherche la minute exacte.
 */

/** Un choix parmi des valeurs, présenté comme une liste déroulante étiquetée. */
function Choix<T extends number | string>({
  label,
  valeur,
  valeurs,
  format,
  onChanger,
}: {
  label: string;
  valeur: T;
  valeurs: readonly T[];
  format: (v: T) => string;
  onChanger: (v: T) => void;
}) {
  return (
    <span className="inline-flex items-center gap-2">
      <select
        aria-label={label}
        value={String(valeur)}
        onChange={(evenement) => {
          const choisi = valeurs.find((v) => String(v) === evenement.target.value);
          if (choisi !== undefined) onChanger(choisi);
        }}
        className="min-h-touch rounded-lg border-2 border-sky-300 bg-white px-2 font-mono font-bold text-sky-800"
      >
        {valeurs.map((v) => (
          <option key={String(v)} value={String(v)}>
            {format(v)}
          </option>
        ))}
      </select>
    </span>
  );
}

export function EtapeReparer({
  content,
  partie,
  onRegler,
}: {
  content: Content;
  partie: GameState;
  onRegler: (reglage: ReglageProgramme) => void;
}) {
  const programme = programmeDuChapitre(content, partie.chapterId);
  const reglage = reglageCourant(partie, content);
  if (!programme || !reglage) {
    return null;
  }

  const { heures, durees_minutes, grandeurs, comparateurs, seuils } = programme.reglages;
  const changer = (modification: Partial<ReglageProgramme>): void =>
    onRegler({ ...reglage, ...modification });

  const grandeurDe = (id: string) => grandeurs.find((g) => g.id === id);
  const premiereCondition: Condition = {
    grandeur: grandeurs[0]!.id,
    comparateur: comparateurs[0]!.id,
    seuil: seuils[Math.floor(seuils.length / 2)]!,
  };

  return (
    <section className="space-y-4">
      <header>
        <h2 className="text-xl font-bold text-slate-900">{programme.titre}</h2>
        <p className="text-slate-700">{T.reparer.aide}</p>
      </header>

      <div className="rounded-2xl border-2 border-slate-300 bg-white p-4 font-mono text-lg leading-loose text-slate-900">
        <p className="font-bold">{T.reparer.chaqueJour}</p>

        <p className="pl-4">
          <span aria-hidden="true">├─ </span>
          {T.reparer.a}{' '}
          <Choix
            label={T.reparer.choisirHeure}
            valeur={reglage.heure}
            valeurs={heures}
            format={(h) => `${String(h).padStart(2, '0')} ${T.reparer.uniteHeure}`}
            onChanger={(heure) => changer({ heure })}
          />
        </p>

        {reglage.condition === null ? (
          <p className="pl-4 text-slate-500">
            <span aria-hidden="true">│ </span>
            {T.reparer.sansCondition}
          </p>
        ) : (
          <p className="pl-4">
            <span aria-hidden="true">├─ </span>
            <span className="font-bold text-sky-800">{T.reparer.si}</span>{' '}
            <Choix
              label={T.reparer.choisirGrandeur}
              valeur={reglage.condition.grandeur}
              valeurs={grandeurs.map((g) => g.id)}
              format={(id) => grandeurDe(id)?.nom ?? id}
              onChanger={(grandeur) => changer({ condition: { ...reglage.condition!, grandeur } })}
            />{' '}
            <Choix
              label={T.reparer.choisirComparateur}
              valeur={reglage.condition.comparateur}
              valeurs={comparateurs.map((c) => c.id)}
              format={(id) => comparateurs.find((c) => c.id === id)?.nom ?? id}
              onChanger={(comparateur) =>
                changer({ condition: { ...reglage.condition!, comparateur } })
              }
            />{' '}
            <Choix
              label={T.reparer.choisirSeuil}
              valeur={reglage.condition.seuil}
              valeurs={seuils}
              format={(s) => `${s} ${grandeurDe(reglage.condition!.grandeur)?.unite ?? ''}`}
              onChanger={(seuil) => changer({ condition: { ...reglage.condition!, seuil } })}
            />
          </p>
        )}

        <p className="pl-4">
          <span aria-hidden="true">└─ </span>
          {reglage.condition !== null && (
            <span className="font-bold text-sky-800">{T.reparer.alors} </span>
          )}
          {T.reparer.arroserPendant}{' '}
          <Choix
            label={T.reparer.choisirDuree}
            valeur={reglage.duree_minutes}
            valeurs={durees_minutes}
            format={(d) => `${d} ${T.reparer.uniteMinutes}`}
            onChanger={(duree_minutes) => changer({ duree_minutes })}
          />
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {reglage.condition === null ? (
          <button
            type="button"
            onClick={() => changer({ condition: premiereCondition })}
            className="min-h-touch rounded-xl bg-sky-600 px-4 font-semibold text-white hover:bg-sky-700"
          >
            {T.reparer.ajouterCondition}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => changer({ condition: null })}
            className="min-h-touch rounded-xl px-4 font-semibold text-slate-700 ring-2 ring-slate-300 hover:bg-slate-50"
          >
            {T.reparer.retirerCondition}
          </button>
        )}

        {reglage.condition !== null && (
          <p className="text-sm text-slate-600">
            {grandeurDe(reglage.condition.grandeur)?.nom} — {T.reparer.mesureePar}{' '}
            {content.equipements.find(
              (e) => e.id === grandeurDe(reglage.condition!.grandeur)?.equipement,
            )?.nom ?? ''}
          </p>
        )}
      </div>

      <p role="status" className="font-semibold text-slate-700">
        {programmeModifie(partie, content) ? T.reparer.modifie : T.reparer.inchange}
      </p>
    </section>
  );
}
